import { redirect } from "react-router-dom";
import localforage from "localforage";
import { checkIfCollaboratorIsAuthenticated } from "../../auth/auth";
import { checkPermission, RolePermissions } from "../../components/PermissionsContext";
import { enterpriseAPI } from "../..";
import { readHallSettlementMode } from "../../services/hallSettlementMode";
import { negotiationPaymentReady } from "../negotiationReadiness";

export type NegotiationReadiness = {
  orgOnboardingComplete: boolean;
  hasOrgAvatar: boolean;
  paymentMethodReady: boolean;
  hallSettlementMode?: "prepaid" | "legacy";
  negotiationBalanceReady?: boolean;
  activeHallAdCount: number;
  negotiationTutorialReady: boolean;
  eligibleForNegotiation: boolean;
};

/**
 * Route loader for the negotiation family: /negociar, /negociar/ranking and
 * /negociar/historico. In this app the route loader is the authorization
 * boundary, so the `bid` "view" check has to run on every entry, tutorial or
 * not.
 *
 * The order matters and is the whole point of this file. The tutorial persists
 * `inTutorial` in localforage so its guided tour can walk through /negociar
 * before the collaborator has finished onboarding (org logo + payment method).
 * That flag lives in IndexedDB and survives a full page reload, while the
 * in-memory tutorial state (amaryCourse) and the mock-negotiation mode do not.
 * So a permission check placed AFTER the `inTutorial` short-circuit would let a
 * collaborator who lacks `bid` access stay on /negociar just by refreshing: the
 * loader would read the stale flag and return before ever reaching the
 * permission gate.
 *
 * Fix: enforce the real permission FIRST. The tutorial may skip the onboarding
 * gates (they only matter for real bidding, and the tour runs on mock data), but
 * it must never skip authorization. During the live tour the session is in
 * explicit mock mode, so checkPermission returns true there by design; once the
 * page reloads that mock mode is gone and the real permission is what answers.
 */
export async function negotiateLoader() {
  const isLogged = await checkIfCollaboratorIsAuthenticated();
  if (!isLogged) {
    return redirect("/auth");
  }

  // Authorization boundary: always enforced, including while a tutorial is
  // active or its persisted flag is still around after a reload.
  const permissions = (await localforage.getItem("permissions")) as RolePermissions | null;
  if (!permissions) return redirect("/auth");
  if (!checkPermission(permissions.bid, "view")) {
    return redirect("/403");
  }

  // Past authorization: the tutorial tour is allowed to skip the onboarding
  // gates below (org logo + payment method) because it runs on mock data.
  const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
  if (isInTutorial) {
    return false;
  }

  const JWT = (await localforage.getItem("JWT")) as string | undefined;
  if (!JWT) return redirect("/auth");
  const response = await enterpriseAPI.get<NegotiationReadiness>("/negotiation-readiness", {
    headers: { "X-JWT": JWT },
  });
  const readiness = response.data;

  if (!readiness.orgOnboardingComplete || !readiness.hasOrgAvatar) {
    return redirect("/organizacao/info");
  }
  // Hall uses its server-declared cycle rail. A prepaid account needs no
  // weekly Pix authorization; radio/TV retain paymentMethodGate's weekly gate.
  const currentMode = await readHallSettlementMode(JWT, readiness);
  if (!negotiationPaymentReady({ ...readiness, hallSettlementMode: currentMode })) {
    return redirect("/organizacao/pagamentos");
  }
  if (readiness.activeHallAdCount < 1) {
    return redirect("/anuncios/atrio");
  }
  if (!readiness.negotiationTutorialReady) {
    return redirect("/suporte");
  }

  return false;
}
